import React from 'react';

/* CURRENT COMPONENT FILES */
import styles from './index.module.less';
import type { Theme } from '../../../core/types';
import { Color, FontType, Align, Type, Size } from '../../../core/enums';
import type CardSchema from '../../../core/schemas/Card.schema';
import Button from '../../archived/button';
import { CardArt } from '../../atoms/card-art';
import { Row, Column } from '../../atoms/grid';
import { Icon } from '../../atoms/icon';
import { Typography } from '../../archived/typography';
import Element from '../../../core/Element';

interface Customization {
  $fontColor?: Color;
  $paddingTop?: number | string;
  theme?: {
    cardTitleTheme?: Theme;
    cardSubTitleTheme?: Theme;
    feeElementTheme?: FeeElementTheme;
  };
}

interface FeeElementTheme {
  typographyTheme?: Theme;
  bodyTheme?: Theme;
  titleTheme?: Theme;
}

interface Props {
  data: CardSchema;
  customization: Customization;
  contentAfterCTA?: React.ReactNode;
  contentAfterCardArt?: React.ReactNode;
  qeId?: string;
}

const getFontColor = (customization: Customization) =>
  customization.$fontColor || Color.DeepBlue;

const cardElement = ({ cardUrl, header, cardArtAlt }: CardSchema) => (
  <CardArt
    src={cardUrl}
    alt={cardArtAlt || header.title}
    theme={{ horizontallyFluid: true }}
  />
);

const cardTitleText = (
  { header }: CardSchema,
  theme?: Theme,
  qeId = 'CardTitleText',
  subQeId = 'CardSubNameText'
) => (
  <>
    <Element
      qeId={qeId}
      Component={header.Component || 'p'}
      theme={{
        padding: 'none',
        margin: 'none',
        color: Color.Black,
        textBody: 4,
        ...(theme || {}),
      }}
    >
      {header.title}
    </Element>
    {header.subName && (
      <Element
        qeId={subQeId}
        Component={header.Component || 'p'}
        theme={{
          fontType: FontType.HelveticaRegular,
          textLegal: 1,
          color: Color.DeepBlue,
          textAlign: Align.Left,
        }}
      >
        {header.subName}
      </Element>
    )}
  </>
);

const cardSubtitleText = (
  data: CardSchema,
  theme: Theme,
  qeId = 'CardSubtitleText'
) => (
  <Element
    Component="p"
    qeId={qeId}
    theme={{
      padding: 'none',
      margin: 'none',
      textHeading: 7,
      ...theme,
    }}
  >
    <Element Component="span">{data.header.subtitle}</Element>
  </Element>
);

const feeElement = (
  { body }: { body: CardSchema['body'] },
  customization: Customization,
  theme: FeeElementTheme = {},
  qeId = 'FeeElement'
) => (
  <Element
    Component="p"
    qeId={qeId}
    theme={{
      color: getFontColor(customization),
      textBody: 3,
      ...(theme.typographyTheme || {}),
    }}
  >
    {Array.isArray(body) &&
      body.map(({ title, description }, i: number) => (
        <Element
          key={title + i}
          Component="p"
          theme={{
            padding: 'none',
            margin: 'none',
            paddingTop: 20,
            ...(theme.bodyTheme || {}),
          }}
        >
          <Element
            Component="span"
            theme={{
              fontType: FontType.HelveticaBold,
              ...(theme.titleTheme || {}),
            }}
          >
            {title}
          </Element>{' '}
          <Element Component="span">{description}</Element>
        </Element>
      ))}
  </Element>
);

const buttonElement = (
  { button }: CardSchema,
  theme: Theme,
  renderRow: boolean,
  qeId = 'SideRailButtonElement'
) => {
  if (!button) {
    return null;
  }
  const content = (
    <Button
      qeId={qeId}
      theme={{
        padding: 'none',
        horizontallyFluid: true,
        marginTop: theme.marginTop,
        marginBottom: theme.marginBottom,
      }}
      $type={Type.Tertiary}
      onClick={button.onClick}
    >
      <Element
        theme={{ horizontallyFluid: true }}
        className={styles.linkButton}
      >
        {button.icon && (
          <Icon
            {...button.icon}
            theme={{
              color: Color.BrightBlue,
              paddingRight: 5,
            }}
            $size={Size.SM}
          />
        )}
        <Typography
          $size={15}
          $family={FontType.HelveticaRegular}
          theme={{
            color: Color.BrightBlue,
            margin: 0,
          }}
        >
          {button.text}
        </Typography>
      </Element>
    </Button>
  );
  return renderRow ? <Row>{content}</Row> : content;
};

/**
 * @deprecated
 * DEPRECATION NOTICE:
 * This component should not be used and will be removed [in V2](https://github.aexp.com/amex-eng/aqx-components/tree/next)
 *
 * Please discuss options with design when possible.
 * Shop Platform engineers and designers are available to assist during bi-weekly office hours.
 */
export const Mobile = ({
  data,
  customization = {
    $fontColor: Color.DeepBlue,
    $paddingTop: 30,
    theme: {
      cardTitleTheme: {},
      cardSubTitleTheme: {},
      feeElementTheme: {
        typographyTheme: {},
        bodyTheme: {},
        titleTheme: {},
      },
    },
  },
  contentAfterCardArt,
  qeId = 'Mobile',
}: Props) => {
  const theme = customization.theme || {};
  return (
    <Element
      qeId={qeId}
      theme={{
        color: getFontColor(customization),
      }}
    >
      <Row>
        <Column $sm="5">{cardElement(data)}</Column>
        <Column $sm="7">{cardTitleText(data, theme.cardTitleTheme)}</Column>
      </Row>
      {contentAfterCardArt}
      {cardSubtitleText(data, {
        color: getFontColor(customization),
        paddingTop: customization.$paddingTop,
        ...(theme.cardSubTitleTheme || {}),
      })}
      {feeElement(data, customization, theme.feeElementTheme)}
    </Element>
  );
};

/**
 * @deprecated
 * DEPRECATION NOTICE:
 * This component should not be used and will be removed [in V2](https://github.aexp.com/amex-eng/aqx-components/tree/next)
 *
 * Please discuss options with design when possible.
 * Shop Platform engineers and designers are available to assist during bi-weekly office hours.
 */
export const Tablet = ({
  data,
  customization = {
    $fontColor: Color.DeepBlue,
    theme: {
      cardTitleTheme: {},
      cardSubTitleTheme: {},
      feeElementTheme: {
        typographyTheme: {},
        bodyTheme: {},
        titleTheme: {},
      },
    },
  },
  contentAfterCTA,
  contentAfterCardArt,
  qeId = 'Tablet',
}: Props) => {
  const theme = customization.theme || {};
  return (
    <Element
      qeId={qeId}
      theme={{
        color: getFontColor(customization),
      }}
    >
      {buttonElement(data, { marginTop: 15, marginBottom: 15 }, true)}
      <Row>
        <Column $md="3">
          {cardElement(data)}
          {contentAfterCardArt}
        </Column>
        <Column $md="7">
          {cardTitleText(data, theme.cardTitleTheme)}
          {cardSubtitleText(data, {
            color: getFontColor(customization),
            paddingTop: 15,
            ...(theme.cardSubTitleTheme || {}),
          })}
          {feeElement(data, customization, theme.feeElementTheme)}
          {contentAfterCTA && contentAfterCTA}
        </Column>
      </Row>
    </Element>
  );
};

/**
 * @deprecated
 * DEPRECATION NOTICE:
 * This component should not be used and will be removed [in V2](https://github.aexp.com/amex-eng/aqx-components/tree/next)
 *
 * Please discuss options with design when possible.
 * Shop Platform engineers and designers are available to assist during bi-weekly office hours.
 */
export const Desktop = ({
  data,
  contentAfterCardArt,
  customization = {
    $fontColor: Color.DeepBlue,
    theme: {
      cardTitleTheme: {},
      cardSubTitleTheme: {},
      feeElementTheme: {
        typographyTheme: {},
        bodyTheme: {},
        titleTheme: {},
      },
    },
  },
  qeId = 'Desktop',
}: Props) => {
  const theme = customization.theme || {};
  return (
    <div data-qe-id={qeId}>
      {buttonElement(data, { marginTop: 30, marginBottom: 0 }, false)}
      {cardTitleText(data, {
        paddingBottom: 20,
        ...(theme.cardTitleTheme || {}),
      })}
      {cardElement(data)}
      {contentAfterCardArt}
      {cardSubtitleText(data, {
        color: getFontColor(customization),
        paddingTop: 30,
        ...(theme.cardSubTitleTheme || {}),
      })}
      {feeElement(data, customization, theme.feeElementTheme)}
    </div>
  );
};
